export interface VersionInfo {
  major: number
  minor: number
  patch: number
  raw: string
}

export interface MigrationResult {
  success: boolean
  message: string
  migratedData?: any
}

export class VersionManager {
  private static readonly CURRENT_VERSION = '1.0.0'
  
  static parseVersion(version: string): VersionInfo {
    const parts = version.split('.')
    return {
      major: parseInt(parts[0] || '0'),
      minor: parseInt(parts[1] || '0'),
      patch: parseInt(parts[2] || '0'),
      raw: version
    }
  }
  
  static isCompatible(version: string): boolean {
    const current = this.parseVersion(this.CURRENT_VERSION)
    const target = this.parseVersion(version)
    
    // メジャーバージョンが異なる場合は非互換
    if (target.major !== current.major) {
      return false
    }
    
    // マイナーバージョンが現在より新しい場合は非互換
    if (target.minor > current.minor) {
      return false
    }
    
    return true
  }
  
  static needsMigration(version: string): boolean {
    const current = this.parseVersion(this.CURRENT_VERSION)
    const target = this.parseVersion(version)
    
    // バージョンが完全に一致する場合はマイグレーション不要
    if (target.major === current.major && 
        target.minor === current.minor && 
        target.patch === current.patch) {
      return false
    }
    
    // 互換性がある場合のみマイグレーション可能
    return this.isCompatible(version)
  }
  
  static migrate(data: any, fromVersion: string): MigrationResult {
    if (!this.isCompatible(fromVersion)) {
      return {
        success: false,
        message: `バージョン ${fromVersion} からのマイグレーションはサポートされていません`
      }
    }
    
    if (!this.needsMigration(fromVersion)) {
      return {
        success: true,
        message: 'マイグレーションは不要です',
        migratedData: data
      }
    }
    
    const from = this.parseVersion(fromVersion)
    const current = this.parseVersion(this.CURRENT_VERSION)
    
    let migratedData = { ...data }
    
    // バージョン0.9.xから1.0.0へのマイグレーション例
    if (from.major === 0 && from.minor === 9) {
      // データ構造の変更があれば処理
      // 例: 古いフィールド名を新しいフィールド名に変更
      if (migratedData.oldFieldName) {
        migratedData.newFieldName = migratedData.oldFieldName
        delete migratedData.oldFieldName
      }
    }
    
    // アイテムデータのマイグレーション（古いorigin形式から新しいoriginStory形式へ）
    if (migratedData.data && migratedData.data.items) {
      migratedData.data.items = this.migrateItems(migratedData.data.items)
    }
    
    // コミュニティデータのマイグレーション（古い構造から新しい簡素化構造へ）
    if (migratedData.data && migratedData.data.communities) {
      migratedData.data.communities = this.migrateCommunities(migratedData.data.communities)
    }
    
    // バージョンを更新
    migratedData.version = this.CURRENT_VERSION
    
    return {
      success: true,
      message: `バージョン ${fromVersion} から ${this.CURRENT_VERSION} へマイグレーションしました`,
      migratedData
    }
  }
  
  private static migrateItems(items: any[]): any[] {
    return items.map(item => {
      if (!item.result || !item.result.lore) {
        return item
      }
      
      const lore = item.result.lore
      
      // originからoriginStoryへの変換（必要な場合）
      // 両方の形式をサポートするため、既にoriginStoryがある場合は変換しない
      if (lore.origin && !lore.originStory) {
        // 古い形式のoriginを保持（互換性のため）
        // 新しいコードでは両方の形式をサポート
      }
      
      // 古いフィールドの削除（stackable, tradeable等）
      if (item.result.basicInfo) {
        delete item.result.basicInfo.stackable
        delete item.result.basicInfo.tradeable
        delete item.result.basicInfo.consumable
        delete item.result.basicInfo.questItem
      }
      
      // 古い価格フィールドの削除
      if (item.result.acquisition) {
        delete item.result.acquisition.basePrice
        delete item.result.acquisition.marketValue
        delete item.result.acquisition.blackMarketValue
        delete item.result.acquisition.crafting
        delete item.result.acquisition.merchantLocations
        delete item.result.acquisition.dropSources
        delete item.result.acquisition.requiredCondition
      }
      
      // 古いstatsフィールドの削除
      if (item.result.effects) {
        delete item.result.effects.stats
      }
      
      return item
    })
  }
  
  private static migrateCommunities(communities: any[]): any[] {
    return communities.map(community => {
      if (!community.result) {
        return community
      }
      
      const result = community.result
      
      // 古い構造から新しい構造への変換
      if (result.geography || result.economy || result.culture || result.politics || result.problems) {
        const convertedResult = {
          basicInfo: result.basicInfo,
          features: {
            geography: result.geography ? 
              `${result.geography.location || ''}。地形: ${result.geography.terrain?.join('、') || ''}。気候: ${result.geography.climate || ''}` : 
              result.features?.geography || '地理情報なし',
            facilities: result.facilities?.mainFacilities ? 
              result.facilities.mainFacilities.map((f: any) => f.name).join('、') : 
              result.features?.facilities || '主要施設情報なし',
            culture: result.culture ? 
              `言語: ${result.culture.languages?.map((l: any) => l.name).join('、') || ''}。宗教: ${result.culture.religions?.map((r: any) => r.name).join('、') || ''}` : 
              result.features?.culture || '文化情報なし',
            problems: result.problems?.currentIssues ? 
              result.problems.currentIssues.map((i: any) => `${i.type}: ${i.description}`).join('。') : 
              result.features?.problems || '特に問題なし'
          },
          residents: {
            totalPopulation: result.residents?.totalPopulation || result.basicInfo.population || 0,
            characteristics: result.residents?.typicalResident || result.residents?.characteristics || '一般的な住民',
            notableResidents: result.residents?.notableResidents?.map((r: any) => ({
              name: r.name,
              occupation: r.occupation,
              description: r.description || r.personality || ''
            })) || []
          },
          politicsEconomy: result.politicsEconomy || {
            politics: result.politics ? 
              `${result.politics.governmentType || ''}。${result.politics.ruler ? `統治者: ${result.politics.ruler.name}` : ''}` : 
              '政治情報なし',
            economy: result.economy ? 
              `豊かさ: ${result.economy.wealthLevel || ''}。主要産業: ${result.economy.mainIndustries?.map((i: any) => i.name).join('、') || ''}` : 
              '経済情報なし'
          },
          history: result.history || {
            foundingStory: result.history?.founding ? 
              `${result.history.founding.year}年に${result.history.founding.founder}によって創立。${result.history.founding.story || ''}` : 
              '創立物語なし',
            timeline: result.history?.majorEvents?.map((e: any) => ({
              year: e.year,
              event: e.event
            })) || []
          },
          metadata: result.metadata || {
            generatedAt: new Date().toISOString(),
            version: '1.0'
          }
        }
        
        // 変換した結果で置き換え
        community.result = convertedResult
      }
      
      return community
    })
  }
  
  static validateDataStructure(data: any): boolean {
    // 古い形式のサポート - dataフィールドが直接配列の場合
    if (!data.data && !data.version) {
      // 古い個別エクスポート形式をチェック
      if (data.names || data.plots || data.titles || data.characters || 
          data.relationships || data.wikipedias || data.imagePrompts || 
          data.enemies || data.items || data.worlds) {
        // 古い形式を新しい形式に変換
        const convertedData = {
          version: '1.0.0',
          exportDate: data.exportDate || new Date().toISOString(),
          dataTypes: [] as string[],
          statistics: { totalItems: 0, byType: {} },
          data: {} as Record<string, any>
        }
        
        // 各データタイプをチェックして変換
        const allTypes = ['names', 'plots', 'titles', 'characters', 
                         'relationships', 'wikipedias', 'imagePrompts',
                         'enemies', 'items', 'worlds', 'skills', 
                         'communities', 'dungeons', 'societies', 'scenes']
        
        for (const type of allTypes) {
          if (data[type] && Array.isArray(data[type])) {
            convertedData.data[type] = data[type]
            convertedData.dataTypes.push(type)
          }
        }
        
        // 変換したデータで元のオブジェクトを更新
        Object.assign(data, convertedData)
        return true
      }
    }
    
    // 必須フィールドの存在チェック（より柔軟に）
    if (!data.data) {
      return false
    }
    
    // versionとexportDateが無い場合はデフォルト値を設定
    if (!data.version) {
      data.version = '1.0.0'
    }
    if (!data.exportDate) {
      data.exportDate = new Date().toISOString()
    }
    
    // データタイプのチェック
    if (data.dataTypes && !Array.isArray(data.dataTypes)) {
      return false
    }
    
    // 全ての有効なデータタイプ（新旧両方をサポート）
    const validTypes = [
      'names', 'plots', 'titles', 'characters', 
      'relationships', 'wikipedias', 'imagePrompts',
      'enemies', 'items', 'worlds' // 新しく追加されたタイプ
    ]
    
    // dataTypesが存在する場合のみチェック（後方互換性）
    if (data.dataTypes) {
      for (const type of data.dataTypes) {
        // 未知のタイプは無視（前方互換性）
        if (validTypes.includes(type)) {
          // 各タイプのデータが配列であることを確認
          if (data.data[type] && !Array.isArray(data.data[type])) {
            return false
          }
        }
      }
    } else {
      // dataTypesが無い場合は、dataオブジェクトから推測
      const dataTypes = []
      for (const type of validTypes) {
        if (data.data[type] && Array.isArray(data.data[type])) {
          dataTypes.push(type)
        }
      }
      data.dataTypes = dataTypes
    }
    
    return true
  }
  
  static getCurrentVersion(): string {
    return this.CURRENT_VERSION
  }
}